// pkg174 — LawPay cell shows est. royalty (card volume × Tier-1 0.20%, labeled estimated) and the summary strip gains "LawPay est. royalty" from lawpay_totals; ACH never counts — delivered 2026-10-03
// pkg173a — LawPay column (connected · TEST/LIVE · since · payment count + operating/trust volume, reconnect warning) on the firms table and mobile cards; integers read off the firms row, no actions — delivered 2026-10-03
// pkg153 — Onboarding Emails card REMOVED (moved to its own page, AdminHQOnboardingEmailsPage, with an HQ-shell nav link); the pkg151/151a state, handlers, catalog load, and card are all extracted — delivered 2026-08-18
// pkg151a — first-name input loses its example placeholder (no personal names in the empty field) — delivered 2026-08-18
// pkg151 — Onboarding Emails card (D-151c): always-visible collapsible card below the firm list — template picker, recipient + first-name inputs, iframe preview (srcDoc), confirm-then-send via hqSendOnboardingEmail; catalog loads best-effort like the other queues — delivered 2026-08-18
// pkg147 — Applications queue (D-147c/d/e): pending applications from lawmighty.com surface in "Needs you"; Approve = bar-verified confirm → server mints the invite + EMAILS the signup link automatically (token echoed in the panel as the bounce fallback); Reject = two-step confirm choosing decline-email vs silent — delivered 2026-08-15
// pkg71 — referral visibility (D-71g): "ref: <code> ✓" rides the Firm & Contact subtext (desktop) and the card meta (mobile) — the code is the referrer's subdomain at signup; ✓ = give-5-get-5 vested — delivered 2026-07-16
// pkg87 — HQ growth console (D-87a-v): MRR strip computed locally (D-87g/h); "Needs you" queue — imminent data purge (D-87u), firms whose AI runs are failing (D-87i), held subdomains (D-87s), outstanding invites; per-firm usage counters + margin, suppressed to n/a for exempt/granted firms and "thin" under 5 runs (D-87e/f); row actions — extend, suspend (D-87p), billing exemption behind a typed-subdomain confirm (D-87q); invite queue with manual bar verification (D-87j-o); subdomain review queue (D-87s/t). Every number here is an integer READ off the firms row — the nightly hqRollup job put it there and holds the only bypass; no client name or document is reachable from this console — delivered 2026-07-15
// pkg80 — D-80b: mobile-friendly firm list — below 700px the table becomes stacked cards (subdomain + status chip, firm, stacked contact lines, dates, full-width Extend buttons); desktop table unchanged; ExtendButtons shared by both layouts — delivered 2026-07-14
// pkg79 — LawMighty HQ operator portal MVP (D-79-r2, a–f): the whole hq.lawmighty.com experience — summary strip + every-tenant-firm table + Extend trial +30/+90 replacing the manual SQL; firms-table metadata ONLY (D-79d) — delivered 2026-07-13
import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../utils/adminApi';

const font = { fontFamily: 'Arial, sans-serif' };
const NAVY = '#1E2761';
const NAVY_DEEP = '#0F1838';
const TERRA = '#B85042';

const STATUS_CHIPS = {
  paid:                 { background: '#d4edda', color: '#155724' },
  trial:                { background: '#e8f0fb', color: '#2f5496' },
  trial_expired:        { background: '#f8d7da', color: '#721c24' },
  past_due:             { background: '#f8d7da', color: '#721c24' },
  pending_verification: { background: '#fdf6e6', color: '#7c5200' },
  canceled:             { background: '#eee',    color: '#666'    },
  suspended:            { background: '#f8d7da', color: '#721c24' },
  exempt:               { background: '#F0F2F8', color: NAVY      },
};

function StatusChip({ status }) {
  const s = STATUS_CHIPS[status.key] || STATUS_CHIPS.canceled;
  return (
    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 9, fontWeight: 600, whiteSpace: 'nowrap', ...s }}>
      {status.label}
    </span>
  );
}

function useIsNarrow(px = 700) {
  const query = '(max-width: ' + px + 'px)';
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return narrow;
}

// pkg87 (D-87e/f) — margin is suppressed far more often than it is shown, and
// that is the point. A pilot's granted credits cost real API dollars and earn
// zero revenue; a percentage there would be fiction. The backend decides
// (usage.margin_note); this only renders the verdict.
const MARGIN_NOTE = {
  exempt:     { text: 'n/a',  title: 'Complimentary firm — no revenue by definition.' },
  granted:    { text: 'n/a',  title: 'Holds granted credits (pilot/HQ grant). That work costs real API dollars and earns no revenue, so a margin number here would be fiction.' },
  thin:       { text: 'thin', title: 'Fewer than 5 runs — too small a sample to mean anything.' },
  no_revenue: { text: 'n/a',  title: 'No credits ever purchased.' },
};

// pkg173a — LawPay cell: counters only, no actions (the firm owner manages
// the connection inside their own portal).
function LawPayCell({ lp }) {
  if (!lp || !lp.connected) return <span style={{ color: '#bbb' }}>—</span>;
  const money = n => '$' + (+n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return (
    <span title={`Connected ${lp.connected_at ? new Date(lp.connected_at).toLocaleDateString('en-US') : ''} · ${lp.payments} payment${lp.payments === 1 ? '' : 's'} · operating ${money(lp.volume_operating)} · trust ${money(lp.volume_trust)}${lp.last_payment_at ? ' · last ' + new Date(lp.last_payment_at).toLocaleDateString('en-US') : ''}`}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, padding: '1px 6px', borderRadius: 8, background: lp.mode === 'live' ? '#e4f3e8' : '#fff3e0', color: lp.mode === 'live' ? '#1e6b35' : '#9a4b00' }}>
        {lp.mode === 'live' ? 'LIVE' : 'TEST'}
      </span>
      {lp.needs_reconnect && <span style={{ color: '#B85042', marginLeft: 4 }} title="LawPay rejected the stored credentials — the owner must reconnect">⚠</span>}
      <span style={{ marginLeft: 6 }}>{lp.payments}</span>
      {(lp.volume_operating > 0 || lp.volume_trust > 0) && <span style={{ color: '#888', marginLeft: 4 }}>{money(lp.volume_operating + lp.volume_trust)}</span>}
      {lp.est_royalty > 0 && <span style={{ color: '#1e6b35', marginLeft: 6 }} title={`Estimated LawMighty royalty: ${money(lp.volume_card)} card volume × 0.20% (Tier 1, standard pricing). ACH excluded. Final figure comes from 8am's monthly report.`}>≈${(+lp.est_royalty).toFixed(2)}</span>}
    </span>
  );
}

function Margin({ usage }) {
  if (usage.margin === null || usage.margin === undefined) {
    const n = MARGIN_NOTE[usage.margin_note] || MARGIN_NOTE.no_revenue;
    return <span style={{ color: '#bbb', fontSize: 12 }} title={n.title}>{n.text}</span>;
  }
  const good = usage.margin >= 70;
  return (
    <span style={{ color: good ? '#155724' : '#7c5200', fontWeight: 600 }}
          title={'$' + usage.api_cost_dollars.toFixed(2) + ' API cost against ' + usage.credits_purchased + ' purchased credits.'}>
      {usage.margin}%
    </span>
  );
}

function UsageCell({ usage }) {
  return (
    <span title={usage.credits_spent + ' spent · ' + usage.credits_purchased + ' purchased · ' + usage.credits_granted + ' granted'}>
      {usage.credits_spent}
      <span style={{ color: '#aaa' }}> / ${usage.api_cost_dollars.toFixed(2)}</span>
    </span>
  );
}

function RunsCell({ usage }) {
  return (
    <span>
      {usage.runs}
      {usage.runs_failed > 0 && (
        <span style={{ color: usage.failing ? TERRA : '#aaa' }} title={usage.runs_failed_7d + ' failed in the last 7 days'}>
          {' '}· {usage.runs_failed} failed
        </span>
      )}
    </span>
  );
}

function ExtendButtons({ firm, busy, onExtend, block }) {
  const base = {
    background: 'none', border: '1px solid ' + NAVY, color: NAVY,
    borderRadius: 3, cursor: 'pointer', fontSize: block ? 12 : 11,
    padding: block ? '7px 0' : '2px 8px',
  };
  return (
    <div style={block ? { display: 'flex', gap: 8, marginTop: 10 } : { display: 'inline' }}>
      <button onClick={() => onExtend(firm, 30)} disabled={!!busy}
        style={{ ...base, ...(block ? { flex: 1 } : { marginRight: 4 }) }}
        title="Extend this firm's trial by 30 days (from today if already expired)">
        {busy === firm.id + ':30' ? '…' : block ? 'Extend +30 days' : '+30'}
      </button>
      <button onClick={() => onExtend(firm, 90)} disabled={!!busy}
        style={{ ...base, ...(block ? { flex: 1 } : {}) }}
        title="Extend this firm's trial by 90 days (from today if already expired)">
        {busy === firm.id + ':90' ? '…' : block ? 'Extend +90 days' : '+90'}
      </button>
    </div>
  );
}

// pkg87 (D-87p/q) — the actions that retire the TablePlus session.
function RowActions({ firm, busy, onExtend, onSuspend, onExemption, hideExtend }) {
  const extendable = !hideExtend && ['trial', 'trial_expired', 'pending_verification', 'canceled'].includes(firm.status.key);
  const suspended  = firm.status.key === 'suspended';
  const link = { background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, padding: 0, textDecoration: 'underline' };
  return (
    <div style={{ whiteSpace: 'nowrap' }}>
      {extendable && <ExtendButtons firm={firm} busy={busy} onExtend={onExtend} />}
      <div style={{ marginTop: extendable ? 5 : 0, display: 'flex', gap: 10 }}>
        {!firm.exempt && (
          <button onClick={() => onSuspend(firm, !suspended)} disabled={!!busy}
            style={{ ...link, color: '#666' }}
            title={suspended ? 'Make this firm routable again' : 'Hard-lock this firm (reversible)'}>
            {suspended ? 'Un-suspend' : 'Suspend'}
          </button>
        )}
        <button onClick={() => onExemption(firm)} disabled={!!busy}
          style={{ ...link, color: TERRA }}
          title="Change whether this firm is billed at all. Requires typing the subdomain.">
          {firm.exempt ? 'Remove exemption' : 'Make exempt'}
        </button>
      </div>
    </div>
  );
}

function FirmCard({ firm: f, busy, onExtend, onSuspend, onExemption }) {
  const meta = { fontSize: 11, color: '#888' };
  const extendable = ['trial', 'trial_expired', 'pending_verification', 'canceled'].includes(f.status.key);
  return (
    <div style={{ border: '1px solid #e4e9f0', borderRadius: 6, padding: '10px 12px', marginBottom: 10, ...font }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{f.subdomain}</span>
        <StatusChip status={f.status} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>{f.firm_name}</div>
      <div style={{ marginTop: 4 }}>
        {f.contact_name  && <div style={meta}>{f.contact_name}</div>}
        {f.contact_email && <div style={meta}>{f.contact_email}</div>}
        {!f.contact_name && !f.contact_email && <div style={meta}>—</div>}
        {f.referred_by && ( /* pkg71 (D-71g) */
          <div style={meta}>
            ref: <span style={{ fontFamily: 'monospace' }}>{f.referred_by}</span>
            {f.referral_rewarded ? ' ✓' : ' · pending'}
          </div>
        )}
      </div>
      {f.usage && (
        <div style={{ ...meta, marginTop: 6 }}>
          <RunsCell usage={f.usage} /> · <UsageCell usage={f.usage} /> · margin <Margin usage={f.usage} /> · LawPay <LawPayCell lp={f.lawpay} />
        </div>
      )}
      <div style={{ ...meta, marginTop: 6 }}>
        Signed up {f.created_at ? new Date(f.created_at).toLocaleDateString() : '—'}
        {f.status.key === 'paid' && f.first_paid_at &&
          <> · paying since {new Date(f.first_paid_at).toLocaleDateString()}</>}
        {['trial', 'trial_expired'].includes(f.status.key) && f.trial_ends_at &&
          <> · trial {f.status.key === 'trial' ? 'ends' : 'ended'} {new Date(f.trial_ends_at).toLocaleDateString()}</>}
      </div>
      {extendable && <ExtendButtons firm={f} busy={busy} onExtend={onExtend} block />}
      <div style={{ marginTop: 8 }}>
        <RowActions firm={f} busy={busy} onExtend={onExtend} onSuspend={onSuspend} onExemption={onExemption} hideExtend />
      </div>
    </div>
  );
}

function MetricCard({ label, value, color, title }) {
  return (
    <div title={title} style={{ background: '#fafafa', border: '1px solid #ddd', borderRadius: 4, padding: 10, textAlign: 'center' }}>
      <div style={{ fontSize: 18, fontWeight: 600, ...font, color: color || NAVY_DEEP }}>{value}</div>
      <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', ...font }}>{label}</div>
    </div>
  );
}

// pkg87 — a "Needs you" banner. Left accent, square corners (a rounded radius
// against a single-sided border reads as a rendering bug).
function Banner({ tone, title, detail, actionLabel, onAction, busy }) {
  const tones = {
    danger:  { bg: '#fdecea', border: '#c0392b', text: '#721c24' },
    warning: { bg: '#fdf6e6', border: '#c98a00', text: '#7c5200' },
    info:    { bg: '#eef3fc', border: NAVY,      text: '#2f5496' },
  };
  const t = tones[tone] || tones.info;
  return (
    <div style={{ background: t.bg, borderLeft: '3px solid ' + t.border, padding: '10px 12px', marginBottom: 8,
                  display: 'flex', alignItems: 'center', gap: 12, ...font }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{title}</div>
        {detail && <div style={{ fontSize: 12, color: t.text, marginTop: 2 }}>{detail}</div>}
      </div>
      {actionLabel && (
        <button onClick={onAction} disabled={!!busy}
          style={{ background: 'none', border: '1px solid ' + t.border, color: t.text,
                   borderRadius: 3, cursor: 'pointer', fontSize: 12, padding: '4px 10px', whiteSpace: 'nowrap' }}>
          {actionLabel}
        </button>
      )}
    </div>
  );
}

// D-87u — days until trialReclamation permanently deletes a firm. The job
// reclaims at trial_ends_at + 60d for non-exempt, never-subscribed firms;
// 'trial_expired' is the backend's name for exactly that population.
function daysToPurge(f) {
  if (f.status.key !== 'trial_expired' || !f.trial_ends_at) return null;
  const reclaimAt = new Date(f.trial_ends_at).getTime() + 60 * 864e5;
  return Math.ceil((reclaimAt - Date.now()) / 864e5);
}

export default function AdminHQPage() {
  const [firms, setFirms]       = useState([]);
  const [counts, setCounts]     = useState(null);
  const [mrr, setMrr]           = useState(null);
  const [lpTotals, setLpTotals] = useState(null); // pkg174
  const [rollupAt, setRollupAt] = useState(null);
  const [invites, setInvites]   = useState([]);
  const [reviews, setReviews]   = useState([]);
  const [apps, setApps]         = useState([]); // pkg147
  const [showApps, setShowApps] = useState(false); // pkg147
  const [approved, setApproved] = useState(null); // pkg147 — last approval result (token echo)
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [notice, setNotice]     = useState('');
  const [busy, setBusy]         = useState('');
  const [showInvites, setShowInvites] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const [issued, setIssued]     = useState(null);
  const [inv, setInv] = useState({ invitee_name: '', invitee_email: '', bar_state: 'TN', bar_number: '', grant_credits: 20, days: 30 });
  const narrow = useIsNarrow();

  const load = useCallback(() => {
    adminApi.hqListFirms()
      .then(r => {
        setFirms(r.firms || []); setCounts(r.counts || null);
        setMrr(r.mrr || null); setRollupAt(r.rollup_at || null); setLpTotals(r.lawpay_totals || null);
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
    // The queues load independently: a failure in either must never blank the
    // firm list (blank list != empty data).
    adminApi.hqListInvites().then(r => setInvites(r.invites || [])).catch(() => {});
    adminApi.hqListSubdomainReviews().then(r => setReviews(r.reviews || [])).catch(() => {});
    adminApi.hqListApplications().then(r => setApps(r.applications || [])).catch(() => {}); // pkg147
  }, []);
  useEffect(() => { load(); }, [load]);

  async function act(key, fn, ok) {
    setBusy(key); setError(''); setNotice('');
    try { const r = await fn(); setNotice(ok(r)); load(); }
    catch (err) { setError(err.message); }
    finally { setBusy(''); }
  }

  const extend = (firm, days) => act(firm.id + ':' + days,
    () => adminApi.hqExtendTrial(firm.id, days),
    r => firm.subdomain + ' trial extended — now ends ' + new Date(r.trial_ends_at).toLocaleDateString() + '.');

  const suspend = (firm, on) => {
    const msg = on
      ? 'Suspend ' + firm.subdomain + '? The firm becomes unroutable immediately — nobody there can log in. Reversible.'
      : 'Un-suspend ' + firm.subdomain + ' and make it routable again?';
    if (!window.confirm(msg)) return;
    return act(firm.id, () => adminApi.hqSuspendFirm(firm.id, on),
      () => firm.subdomain + (on ? ' suspended.' : ' un-suspended.'));
  };

  // D-87q — exemption silently changes whether a law firm gets charged, so it
  // is the one action that makes you type the name. The server re-checks the
  // typed value and independently refuses while a live Stripe subscription
  // exists — marking a firm complimentary while Stripe keeps charging their
  // card is the worst outcome available here, and it is silent.
  const exemption = (firm) => {
    const on = !firm.exempt;
    const typed = window.prompt(
      (on ? 'MAKE EXEMPT' : 'REMOVE EXEMPTION') + ' — ' + firm.firm_name + '\n\n' +
      (on ? 'This firm stops being billed entirely and is never asked to subscribe.'
          : 'This firm starts being billed: the trial clock and paywall apply again.') +
      '\n\nType the subdomain to confirm:');
    if (typed === null) return;
    return act(firm.id, () => adminApi.hqSetExemption(firm.id, on, typed.trim()),
      () => firm.subdomain + ': billing exemption ' + (on ? 'granted.' : 'removed.'));
  };

  const createInvite = () => {
    if (!inv.invitee_name || !inv.bar_number) {
      setError('Name and bar number are required — the bar check is the point of the queue.'); return;
    }
    return act('invite', () => adminApi.hqCreateInvite(inv), r => {
      setIssued(r);
      setInv({ invitee_name: '', invitee_email: '', bar_state: 'TN', bar_number: '', grant_credits: 20, days: 30 });
      return 'Invite issued. Copy the code below into your own email — HQ does not send it.';
    });
  };


  // pkg147 (D-147c) — approval requires the explicit bar-verified confirm; the
  // server refuses without it. The default 20 credits / 30 days mirror the
  // manual invite form's defaults.
  const approveApp = (a) => {
    if (!window.confirm(
      'APPROVE ' + a.firm_name + ' (' + a.applicant_name + ')?\n\n' +
      'Confirm you verified ' + a.bar_state + ' bar #' + a.bar_number + ' against the state bar\'s own lookup.\n\n' +
      'Approving issues a 30-day single-use invite (20 credits) and EMAILS THE SIGNUP LINK to ' + a.email + ' automatically.')) return;
    return act(a.id, () => adminApi.hqApproveApplication(a.id, { bar_verified: true, grant_credits: 20, days: 30 }), r => {
      setApproved({ ...r, applicant_name: a.applicant_name });
      return a.firm_name + ' approved — signup link emailed to ' + r.email + '.';
    });
  };

  const rejectApp = (a) => {
    const notify = window.confirm(
      'REJECT ' + a.firm_name + ' (' + a.applicant_name + ')?\n\n' +
      'OK = reject AND send the brief decline email.\nCancel = choose silent rejection next.');
    if (!notify && !window.confirm('Reject ' + a.firm_name + ' SILENTLY (no email)?')) return;
    return act(a.id, () => adminApi.hqRejectApplication(a.id, notify),
      () => a.firm_name + ' rejected' + (notify ? ' — decline email sent.' : ' (silent).'));
  };

  const resolveReview = (rv, resolution) => {
    if (resolution === 'approved' && !window.confirm(
      'Approve "' + rv.attempted_subdomain + '"? It goes on the allowlist and their next signup attempt walks straight through. You still have to tell them to try again' +
      (rv.contact_email ? ' — ' + rv.contact_email : ' (no email was captured)') + '.')) return;
    return act(rv.id, () => adminApi.hqResolveSubdomainReview(rv.id, resolution),
      () => '"' + rv.attempted_subdomain + '" ' + resolution + '.');
  };

  if (loading) return <div style={{ padding: 40, ...font, color: '#888' }}>Loading...</div>;

  const purging  = firms.map(f => ({ f, d: daysToPurge(f) })).filter(x => x.d !== null && x.d <= 14 && x.d > -30);
  const failing  = firms.filter(f => f.usage && f.usage.failing);
  const openRevs = reviews.filter(r => !r.resolved_at);
  const openInvs = invites.filter(i => i.state === 'open');
  const pendApps = apps.filter(a => a.status === 'pending'); // pkg147
  const quiet    = !purging.length && !failing.length && !openRevs.length && !openInvs.length && !pendApps.length;
  const inp = { fontSize: 12, padding: '5px 7px', border: '1px solid #ccc', borderRadius: 3, ...font };

  return (
    <div>
      <h1 className="page-title">LawMighty HQ</h1>
      <p className="page-subtitle">
        Every firm on the platform. Usage counters are totals only — no client name, matter, or document is
        reachable from this console.{' '}
        {rollupAt
          ? <span style={{ color: '#999' }}>Usage as of {new Date(rollupAt).toLocaleString()}.</span>
          : <span style={{ color: '#999' }}>Usage counters not computed yet — the nightly rollup first runs ~3 minutes after a deploy.</span>}
      </p>

      {error  && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}

      {mrr && counts && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(115px, 1fr))', gap: 10, marginBottom: 16 }}>
          <MetricCard label="MRR"             value={'$' + mrr.mrr_dollars.toFixed(2)} color="#155724" />
          {lpTotals && <MetricCard label="LawPay est. royalty" value={'≈$' + (+lpTotals.est_royalty).toFixed(2)} color="#155724" title={`${lpTotals.connected} firm(s) connected · ${lpTotals.payments} payments · $${(+lpTotals.card_volume).toLocaleString('en-US')} card volume all-time × 0.20% (Tier 1). Estimated; 8am pays monthly on its own count.`} />}
          <MetricCard label="Paying"          value={mrr.paying} color="#155724" />
          <MetricCard label="In Trial"        value={mrr.trial} color="#2f5496" />
          <MetricCard label="Expired, Unpaid" value={mrr.trial_expired} color="#721c24" />
          {mrr.past_due > 0 && <MetricCard label="Past Due" value={mrr.past_due} color="#721c24" />}
          <MetricCard label="Total Firms"     value={counts.total} />
        </div>
      )}

      <h2 style={{ fontSize: 14, ...font, color: '#555', margin: '0 0 8px' }}>Needs you</h2>
      {quiet && (
        <div style={{ ...font, fontSize: 12, color: '#999', border: '1px dashed #ddd', padding: '10px 12px', marginBottom: 16 }}>
          Nothing needs attention.
        </div>
      )}

      {purging.map(({ f, d }) => (
        <Banner key={'p' + f.id} tone="danger"
          title={d >= 0 ? 'Data purge in ' + d + ' day' + (d === 1 ? '' : 's') + ' — ' + f.firm_name : 'Data purge overdue — ' + f.firm_name}
          detail={'Trial ended ' + new Date(f.trial_ends_at).toLocaleDateString() + ' and they never subscribed. The nightly job frees ' + f.subdomain + ' and permanently deletes their data. Extending stops the clock.'}
          actionLabel="Extend +90" busy={busy} onAction={() => extend(f, 90)} />
      ))}

      {failing.map(f => (
        <Banner key={'f' + f.id} tone="warning"
          title={f.usage.runs_failed_7d + ' failed runs — ' + f.firm_name}
          detail={'In the last 7 days. They have not necessarily told you.' + (f.contact_email ? ' Contact: ' + f.contact_email : '')} />
      ))}

      {openRevs.length > 0 && (
        <Banner tone="warning"
          title={openRevs.length + ' subdomain' + (openRevs.length === 1 ? '' : 's') + ' held for review'}
          detail={openRevs.map(r => '"' + r.attempted_subdomain + '"').join(', ') + ' — they were told to contact support and cannot proceed until you approve.'}
          actionLabel={showReviews ? 'Hide' : 'Review'} onAction={() => setShowReviews(v => !v)} />
      )}

      {pendApps.length > 0 && (
        <Banner tone="warning"
          title={pendApps.length + ' application' + (pendApps.length === 1 ? '' : 's') + ' awaiting review'}
          detail={pendApps.map(a => a.firm_name + ' (' + a.applicant_name + ')').join(', ') + ' — they applied on lawmighty.com and are waiting on you.'}
          actionLabel={showApps ? 'Hide' : 'Review'} onAction={() => setShowApps(v => !v)} />
      )}

      {openInvs.length > 0 && (
        <Banner tone="info"
          title={openInvs.length + ' invite' + (openInvs.length === 1 ? '' : 's') + ' outstanding'}
          detail={openInvs.map(i => i.invitee_name).join(', ') + ' — issued, not yet redeemed.'}
          actionLabel={showInvites ? 'Hide' : 'Open queue'} onAction={() => setShowInvites(v => !v)} />
      )}

      {showApps && (
        <div className="card" style={{ marginBottom: 12 }}>
          <p style={{ ...font, fontSize: 12, color: '#666', marginTop: 0 }}>
            Applications from lawmighty.com. Verify the bar number against the state bar's own lookup <strong>before</strong> approving —
            approval mints the invite and <strong>emails the signup link automatically</strong>. Rejection asks whether to send the decline email.
          </p>
          {approved && (
            <div style={{ background: '#F0F2F8', border: '1px solid ' + NAVY, borderRadius: 4, padding: '10px 12px', marginBottom: 10 }}>
              <div style={{ ...font, fontSize: 12, color: '#666', marginBottom: 4 }}>
                {approved.applicant_name} approved — link emailed to {approved.email}. If that email bounces, the code (expires {new Date(approved.expires_at).toLocaleDateString()}) is:
              </div>
              <code style={{ fontSize: 16, fontWeight: 700, letterSpacing: 1, color: NAVY_DEEP }}>{approved.token}</code>
            </div>
          )}
          {apps.length === 0 ? (
            <p style={{ ...font, fontSize: 12, color: '#999', margin: 0 }}>No applications yet.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, ...font }}>
              <tbody>
                {apps.map(a => (
                  <tr key={a.id} style={{ borderBottom: '1px solid #eee', verticalAlign: 'top' }}>
                    <td style={{ padding: '6px 4px' }}>
                      <strong>{a.firm_name}</strong><br />
                      {a.applicant_name}<span style={{ color: '#999' }}> · {a.email}{a.phone ? ' · ' + a.phone : ''}</span><br />
                      <span style={{ color: '#888' }}>{a.firm_size} · {a.states} · {a.bar_state} bar {a.bar_number}</span>
                      {a.practice_areas && <><br /><span style={{ color: '#888' }}>{a.practice_areas}</span></>}
                      {a.message && <><br /><span style={{ color: '#666', fontStyle: 'italic' }}>&ldquo;{a.message}&rdquo;</span></>}
                    </td>
                    <td style={{ padding: '6px 4px', color: '#888', whiteSpace: 'nowrap' }}>
                      {new Date(a.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '6px 4px', whiteSpace: 'nowrap' }}>
                      {a.status === 'pending'
                        ? <span style={{ color: '#856404' }}>pending</span>
                        : a.status === 'approved'
                          ? <span style={{ color: '#155724' }}>approved{a.redeemed_subdomain ? ' → ' + a.redeemed_subdomain : a.invite_token ? ' · invite open' : ''}</span>
                          : <span style={{ color: '#999' }}>rejected</span>}
                    </td>
                    <td style={{ padding: '6px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {a.status === 'pending' && (
                        <>
                          <button onClick={() => approveApp(a)} disabled={!!busy}
                            style={{ background: NAVY, border: 'none', color: '#fff', borderRadius: 3, cursor: 'pointer', fontSize: 11, padding: '4px 10px', marginRight: 6, ...font }}>
                            Approve
                          </button>
                          <button onClick={() => rejectApp(a)} disabled={!!busy}
                            style={{ background: 'none', border: 'none', color: TERRA, cursor: 'pointer', fontSize: 11, textDecoration: 'underline', padding: 0 }}>
                            reject
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div style={{ margin: '16px 0 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: 14, ...font, color: '#555', margin: 0 }}>Firms</h2>
        <button onClick={() => setShowInvites(v => !v)}
          style={{ background: 'none', border: '1px solid ' + NAVY, color: NAVY, borderRadius: 3, cursor: 'pointer', fontSize: 12, padding: '4px 10px', ...font }}>
          {showInvites ? 'Hide invites' : 'New invite'}
        </button>
      </div>

      {showInvites && (
        <div className="card" style={{ marginBottom: 12 }}>
          <p style={{ ...font, fontSize: 12, color: '#666', marginTop: 0 }}>
            Check the bar number against the state bar's own lookup <strong>before</strong> issuing — an invite cannot
            exist without a recorded verification. HQ generates the code; you paste it into your own email.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <input style={{ ...inp, flex: '2 1 150px' }} placeholder="Attorney name"
              value={inv.invitee_name} onChange={e => setInv({ ...inv, invitee_name: e.target.value })} />
            <input style={{ ...inp, flex: '2 1 160px' }} placeholder="Email (optional)"
              value={inv.invitee_email} onChange={e => setInv({ ...inv, invitee_email: e.target.value })} />
            <input style={{ ...inp, width: 55 }} placeholder="TN" maxLength={2}
              value={inv.bar_state} onChange={e => setInv({ ...inv, bar_state: e.target.value.toUpperCase() })} />
            <input style={{ ...inp, width: 100 }} placeholder="Bar #"
              value={inv.bar_number} onChange={e => setInv({ ...inv, bar_number: e.target.value })} />
            <label style={{ ...font, fontSize: 12, color: '#666' }}>
              credits{' '}
              <input type="number" min="0" max="500" style={{ ...inp, width: 60 }}
                value={inv.grant_credits} onChange={e => setInv({ ...inv, grant_credits: parseInt(e.target.value, 10) || 0 })} />
            </label>
            <button onClick={createInvite} disabled={busy === 'invite'}
              style={{ background: NAVY, border: 'none', color: '#fff', borderRadius: 3, cursor: 'pointer', fontSize: 12, padding: '6px 14px', ...font }}>
              {busy === 'invite' ? 'Issuing…' : 'Issue invite'}
            </button>
          </div>

          {issued && (
            <div style={{ background: '#F0F2F8', border: '1px solid ' + NAVY, borderRadius: 4, padding: '10px 12px', marginBottom: 10 }}>
              <div style={{ ...font, fontSize: 12, color: '#666', marginBottom: 4 }}>
                Invitation code — expires {new Date(issued.expires_at).toLocaleDateString()}, single use, grants {issued.grant_credits} credits on redemption:
              </div>
              <code style={{ fontSize: 16, fontWeight: 700, letterSpacing: 1, color: NAVY_DEEP }}>{issued.token}</code>
            </div>
          )}

          {invites.length === 0 ? (
            <p style={{ ...font, fontSize: 12, color: '#999', margin: 0 }}>No invites yet.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, ...font }}>
              <tbody>
                {invites.map(i => (
                  <tr key={i.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '6px 4px' }}>
                      {i.invitee_name}<span style={{ color: '#999' }}> · {i.bar_state} {i.bar_number}</span>
                    </td>
                    <td style={{ padding: '6px 4px', fontFamily: 'monospace' }}>{i.token}</td>
                    <td style={{ padding: '6px 4px', color: '#888' }}>{i.grant_credits} cr</td>
                    <td style={{ padding: '6px 4px' }}>
                      {i.state === 'redeemed'
                        ? <span style={{ color: '#155724' }}>redeemed → {i.redeemed_subdomain || '—'}</span>
                        : i.state === 'expired'
                          ? <span style={{ color: '#999' }}>expired</span>
                          : <span style={{ color: '#2f5496' }}>open</span>}
                    </td>
                    <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                      {i.state !== 'redeemed' && (
                        <button onClick={() => act(i.id, () => adminApi.hqRevokeInvite(i.id), () => 'Invite revoked.')}
                          disabled={!!busy}
                          style={{ background: 'none', border: 'none', color: TERRA, cursor: 'pointer', fontSize: 11, textDecoration: 'underline', padding: 0 }}>
                          revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showReviews && (
        <div className="card" style={{ marginBottom: 12 }}>
          <p style={{ ...font, fontSize: 12, color: '#666', marginTop: 0 }}>
            These names hit the review tier of the name filter. Approving adds the name to the allowlist so their next
            attempt succeeds — <strong>it does not notify them</strong>. That email is yours to send.
          </p>
          {reviews.length === 0 ? (
            <p style={{ ...font, fontSize: 12, color: '#999', margin: 0 }}>Nothing held.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, ...font }}>
              <tbody>
                {reviews.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '6px 4px', fontFamily: 'monospace' }}>{r.attempted_subdomain}</td>
                    <td style={{ padding: '6px 4px', color: '#888' }}>{r.contact_email || '— no email captured'}</td>
                    <td style={{ padding: '6px 4px', color: '#888' }}>
                      {r.attempt_count} attempt{r.attempt_count === 1 ? '' : 's'} · last {new Date(r.last_seen_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                      {r.resolved_at ? (
                        <span style={{ color: r.resolution === 'approved' ? '#155724' : '#999' }}>{r.resolution}</span>
                      ) : (
                        <>
                          <button onClick={() => resolveReview(r, 'approved')} disabled={!!busy}
                            style={{ background: 'none', border: '1px solid #155724', color: '#155724', borderRadius: 3, cursor: 'pointer', fontSize: 11, padding: '2px 8px', marginRight: 6 }}>
                            Approve
                          </button>
                          <button onClick={() => resolveReview(r, 'rejected')} disabled={!!busy}
                            style={{ background: 'none', border: '1px solid #999', color: '#666', borderRadius: 3, cursor: 'pointer', fontSize: 11, padding: '2px 8px' }}>
                            Reject
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="card">
        {firms.length === 0 ? (
          <p style={{ ...font, fontSize: 13, color: '#888', margin: 0 }}>No firms yet.</p>
        ) : narrow ? (
          <div>
            {firms.map((f) => (
              <FirmCard key={f.id} firm={f} busy={busy} onExtend={extend} onSuspend={suspend} onExemption={exemption} />
            ))}
          </div>
        ) : (
          <div className="table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, ...font }}>
              <thead>
                <tr style={{ background: NAVY_DEEP, color: '#fff' }}>
                  <th style={{ padding: '7px 10px', textAlign: 'left', width: 130 }}>Subdomain</th>
                  <th style={{ padding: '7px 10px', textAlign: 'left' }}>Firm &amp; Contact</th>
                  <th style={{ padding: '7px 10px', textAlign: 'left', width: 150 }}>Status</th>
                  <th style={{ padding: '7px 10px', textAlign: 'right', width: 105 }}>Runs</th>
                  <th style={{ padding: '7px 10px', textAlign: 'right', width: 110 }}>Credits / cost</th>
                  <th style={{ padding: '7px 10px', textAlign: 'right', width: 70 }}>Margin</th>
                  <th style={{ padding: '7px 10px', textAlign: 'right', width: 110 }}>LawPay</th>
                  <th style={{ padding: '7px 10px', width: 165 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {firms.map((f, i) => (
                  <tr key={f.id} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {f.subdomain}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      {f.firm_name}
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                        {[f.contact_name, f.contact_email, f.contact_phone].filter(Boolean).join(' · ') || '—'}
                      </div>
                      {f.referred_by && ( /* pkg71 (D-71g) */
                        <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}
                          title={f.referral_rewarded ? 'Referral reward vested (first paid invoice landed)' : 'Referral pending — vests on this firm\'s first paid invoice'}>
                          ref: <span style={{ fontFamily: 'monospace' }}>{f.referred_by}</span>
                          {f.referral_rewarded ? <span style={{ color: '#1d7a4f' }}> ✓</span> : <span style={{ color: '#bbb' }}> · pending</span>}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <StatusChip status={f.status} />
                      {f.status.key === 'paid' && f.first_paid_at && (
                        <div style={{ fontSize: 10, color: '#888', marginTop: 3 }}>
                          paying since {new Date(f.first_paid_at).toLocaleDateString()}
                        </div>
                      )}
                      {['trial', 'trial_expired'].includes(f.status.key) && f.trial_ends_at && (
                        <div style={{ fontSize: 10, color: '#888', marginTop: 3 }}>
                          trial {f.status.key === 'trial' ? 'ends' : 'ended'} {new Date(f.trial_ends_at).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 12 }}>
                      {f.usage ? <RunsCell usage={f.usage} /> : <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 12 }}>
                      {f.usage ? <UsageCell usage={f.usage} /> : <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 12 }}>
                      {f.usage ? <Margin usage={f.usage} /> : <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 12, whiteSpace: 'nowrap' }}>
                      <LawPayCell lp={f.lawpay} />
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <RowActions firm={f} busy={busy} onExtend={extend} onSuspend={suspend} onExemption={exemption} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p style={{ ...font, fontSize: 11, color: '#999', marginTop: 10 }}>
        Margin is measured against purchased credits only. It reads <em>n/a</em> for complimentary firms and for any
        firm holding granted credits — a pilot's work costs real API dollars and earns no revenue, so a percentage
        there would be fiction — and <em>thin</em> under 5 runs. Counters are recomputed nightly from the credit
        ledger; the ledger is always the source of truth.
      </p>
    </div>
  );
}
